import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Grid,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from "@mui/icons-material/Person";
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import { useDispatch, useSelector } from "react-redux";
import AdminApi from "Service/Admin.api";
import StatusApi from "Service/Status.api";
import OdrApi from "Service/Odr.api";
import PaymentApi from "Service/Payment.api";
import ColorData from "Components/CstmTheme/ColorData";
import { StyledTableCell } from "Pages/AdminList/ProfessionalUserList";
import { IndianStates } from "Constants/Arrays";
import { setAlertData, startLoading, stopLoading } from "../../Redux/Screen/Action";
import { formatUserAddress } from "Pages/AdminList/CaseDetails";

interface CaseDetailsType {
  pkCase: number;
  caseNumber: string;
  clientId: number;
  clientFullName: string;
  clientEmail?: string;
  userType?: string;
  respondentUserType?: string;
  clientContactNumber?: string;
  caseTypeId: number;
  caseTypeName: string;
  orderTypeId: number;
  orderTypeName: string;
  casePrice: number;
  finalCasePrice?: number;
  currentProcessingStatusId: number;
  currentProcessingStatusName: string;
  description: string | null;
  dateCreated: string;
  createdByUserName: string;
  claimAmount: number | null;
  agreementDate: string | null;
  dueDateOfPayment: string | null;
  courtClauseReference: string | null;
  assignedProfessionalId: number | null;
  assignedProfessionalName: string | null;
  assignedAdminId?: number | null;
  assignedAdminName?: string | null;
  assignedProfessionals?: any[];
  claimantEmail?: string;
  claimantPhone?: string;
  respondentName?: string;
  respondentEmail?: string;
  respondentPhone?: string;
}

const DetailItem: React.FC<{ label: string; value: any }> = ({ label, value }) => (
  <Box mb={1.5}>
    <Typography variant="body2" color="textSecondary">{label}</Typography>
    <Typography variant="body1" fontWeight="500">{value}</Typography>
  </Box>
);

const CaseControlCenter: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const numericCaseId: number = Number(id);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const { myInfo } = useSelector((state: any) => state.user);

  // STATE
  const [caseData, setCaseData] = useState<CaseDetailsType | null>(null);
  const [caseStatusList, setCaseStatusList] = useState<any[]>([]);
  const [selectedStatusId, setSelectedStatusId] = useState<number | "">("");
  const [users, setUsers] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  // Admin Assignment state for SuperAdmin
  const [adminList, setAdminList] = useState<any[]>([]);
  const [selectedAdminId, setSelectedAdminId] = useState<number | "">("");
  const [isUpdatingAdmin, setIsUpdatingAdmin] = useState(false);

  // Case Payments (e.g. Chat Box Payments)
  const [casePayments, setCasePayments] = useState<any[]>([]);

  // Invoice Modal State
  const [openInvoiceModal, setOpenInvoiceModal] = useState(false);
  const [selectedPaymentForInvoice, setSelectedPaymentForInvoice] = useState<any>(null);
  const [professionalFees, setProfessionalFees] = useState<number | "">("");
  const [nlpCommission, setNlpCommission] = useState<number | "">("");
  const [outOfPocketExpenses, setOutOfPocketExpenses] = useState<number | "">("");
  const [governmentFees, setGovernmentFees] = useState<number | "">("");
  const [sendEmail, setSendEmail] = useState(false);
  const [isGeneratingInvoice, setIsGeneratingInvoice] = useState(false);

  useEffect(() => {
    if (!isNaN(numericCaseId)) {
      loadAllData();
    }
  }, [numericCaseId]);

  useEffect(() => {
    if (myInfo?.userType?.toUpperCase() === "SUPERADMIN") {
      fetchAdmins();
    }
  }, [myInfo]);

  const fetchAdmins = async () => {
    try {
      const res = await AdminApi.getAllUsers("Admin", 1, 100);
      if (res?.success && Array.isArray(res.data?.data)) {
        setAdminList(res.data.data);
      }
    } catch (err) {
      console.error("Error fetching admin list:", err);
    }
  };

  const handleAssignAdmin = async () => {
    if (!selectedAdminId) return;
    try {
      setIsUpdatingAdmin(true);
      const caseUpdatePayload = {
        pkCase: numericCaseId,
        assignedAdminId: selectedAdminId,
      };
      const response = await AdminApi.updateCaseAssignment(numericCaseId, caseUpdatePayload);
      if (response?.success) {
        dispatch(setAlertData({ alertMessage: "Admin Assigned Successfully", alertVariant: "success", openAlert: true }));
        fetchCaseDetails();
      }
    } catch (error) {
      console.error("Error assigning admin:", error);
      dispatch(setAlertData({ alertMessage: "Error Assigning Admin", alertVariant: "error", openAlert: true }));
    } finally {
      setIsUpdatingAdmin(false);
    }
  };

  const loadAllData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchCaseDetails(),
        fetchCasePayments()
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCasePayments = async () => {
    try {
      const res = await PaymentApi.getPaymentsByCaseId(numericCaseId);
      if (res?.success && Array.isArray(res.data?.data)) {
        setCasePayments(res.data.data);
      }
    } catch (err) {
      console.error("Error fetching case payments:", err);
    }
  };

  const fetchCaseDetails = async () => {
    try {
      const res = await AdminApi.fetchCaseDetails(numericCaseId);
      if (res?.success) {
        setCaseData(res.data);
        setSelectedStatusId(res.data.currentProcessingStatusId || "");

        // Fetch statuses for this case type
        const statusRes = await StatusApi.caseStatus(res.data.caseTypeId);
        if (statusRes?.data) {
          setCaseStatusList(statusRes.data);
        }

        fetchProfessionals(res.data);
      }
    } catch (err) {
      console.error("Error fetching case details:", err);
      dispatch(setAlertData({ alertMessage: "Error fetching details", alertVariant: "error", openAlert: true }));
    }
  };

  const fetchProfessionals = async (currentCaseData: any) => {
    try {
      const response = await AdminApi.GetAllActiveProfessionalUser();
      if (response?.success && Array.isArray(response.data)) {
        //  let activeProfs = response.data;
        // const isOdr = ["Online Mediation", "Online Arbitration", "Arbitration", "Online Negotiation"].includes(currentCaseData.orderTypeName);

        // if (isOdr) {
        //   const role = currentCaseData.orderTypeName === "Online Mediation" ? "Mediator" : (currentCaseData.orderTypeName === "Online Negotiation" ? "Negotiator" : "Arbitrator");
        //   try {
        //     const eligibleRes = await OdrApi.getEligibleProfessionals(role);
        //     if (eligibleRes?.success && Array.isArray(eligibleRes.data)) {
        //       const eligibleIds = eligibleRes.data.map((p: any) => String(p.id));
        //       activeProfs = activeProfs.filter((u: any) => eligibleIds.includes(String(u.id)));
        //     }
        //   } catch (err) {
        //     console.error("Error fetching eligible professionals:", err);
        //   }
        // }
        // setUsers(activeProfs);
        setUsers(response.data);
      }
    } catch (error) {
      console.error("Error fetching professionals:", error);
    }
  };

  // --- ACTIONS ---

  const handleUpdateStatus = async () => {
    if (!selectedStatusId) return;
    try {
      setIsUpdatingStatus(true);
      dispatch(startLoading({ loading: true, loadtext: "Updating Status..." }));
      const caseUpdatePayload = {
        pkCase: numericCaseId,
        statusId: selectedStatusId,
      };
      const response: any = await AdminApi.updateCaseAssignment(numericCaseId, caseUpdatePayload);
      if (response?.success !== false) {
        dispatch(setAlertData({ alertMessage: "Status Updated Successfully", alertVariant: "success", openAlert: true }));
        fetchCaseDetails();
      } else {
        dispatch(setAlertData({ alertMessage: response?.message || response?.data?.message || "Error Updating Status", alertVariant: "error", openAlert: true }));
      }
    } catch (error: any) {
      console.error("Error updating status:", error);
      const msg = error?.response?.data?.message || error?.data?.message || error?.message || "Error Updating Status";
      dispatch(setAlertData({ alertMessage: msg, alertVariant: "error", openAlert: true }));
    } finally {
      setIsUpdatingStatus(false);
      dispatch(stopLoading());
    }
  };

  const handleAssignProfessional = async (professionalId: string) => {
    try {
      dispatch(startLoading({ loading: true, loadtext: "Assigning Professional..." }));
      const isOdr = ["Online Mediation", "Online Arbitration", "Arbitration", "Online Negotiation"].includes(caseData?.orderTypeName || "");
      let success = false;
      let alertMsg = "";

      if (isOdr) {
        const res = await OdrApi.assignOdrNeutral(numericCaseId, { neutralId: Number(professionalId) });
        success = !!res?.success;
        alertMsg = res?.message || (success ? "Neutral assigned." : "Failed to assign neutral.");
      } else {
        const res = await AdminApi.AssignCaseToProfessional({ caseId: String(numericCaseId), assignedProfessionalId: professionalId });
        success = !!res?.success;
        alertMsg = res?.alert?.alertMessage || (success ? "Professional assigned." : "Failed to assign.");
      }

      dispatch(stopLoading());
      dispatch(setAlertData({ alertMessage: alertMsg, alertVariant: success ? "success" : "error", openAlert: true }));
      fetchCaseDetails();
    } catch (error) {
      dispatch(stopLoading());
      dispatch(setAlertData({ alertMessage: "Error assigning professional", alertVariant: "error", openAlert: true }));
    }
  };

  const handleUnassignProfessional = async (professionalId: number) => {
    try {
      dispatch(startLoading({ loading: true, loadtext: "Removing Professional..." }));
      const res = await AdminApi.UnassignCaseProfessional({ caseId: numericCaseId, professionalId });
      dispatch(stopLoading());

      if (res?.success) {
        dispatch(setAlertData({ alertMessage: "Removed successfully.", alertVariant: "success", openAlert: true }));
        fetchCaseDetails();
      } else {
        dispatch(setAlertData({ alertMessage: res?.message || "Failed to remove.", alertVariant: "error", openAlert: true }));
      }
    } catch (error) {
      dispatch(stopLoading());
      dispatch(setAlertData({ alertMessage: "Error removing professional", alertVariant: "error", openAlert: true }));
    }
  };

  const handleGenerateInvoice = async () => {
    try {
      setIsGeneratingInvoice(true);
      const payload = {
        professionalFees: Number(professionalFees) || 0,
        nlpCommission: Number(nlpCommission) || 0,
        outOfPocketExpenses: Number(outOfPocketExpenses) || 0,
        governmentFees: Number(governmentFees) || 0,
        sendEmail: sendEmail,
      };
      const res = await AdminApi.generateZohoInvoice(numericCaseId, payload);
      if (res?.success || res?.data?.success) {
        dispatch(setAlertData({ alertMessage: "Zoho Invoice created successfully!", alertVariant: "success", openAlert: true }));
        setOpenInvoiceModal(false);
      } else {
        dispatch(setAlertData({ alertMessage: res?.message || res?.data?.message || "Failed to create invoice", alertVariant: "error", openAlert: true }));
      }
    } catch (err: any) {
      console.error(err);
      dispatch(setAlertData({ alertMessage: err?.response?.data?.message || "Error generating invoice", alertVariant: "error", openAlert: true }));
    } finally {
      setIsGeneratingInvoice(false);
    }
  };

  const displayedUsers = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return users;
    return users.filter((u: any) =>
      `${u.firstName} ${u.lastName}`.toLowerCase().includes(q) ||
      (u.contactNumber || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q)
    );
  }, [searchQuery, users]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ py: 4, pb: 12, display: "flex", flexDirection: "column", height: "100vh", overflowY: "auto", px: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ fontWeight: "bold" }}>
          Back to Hub
        </Button>
        <Button variant="contained" color="warning" onClick={() => { setSelectedPaymentForInvoice(null); setOpenInvoiceModal(true); }}>
          Generate Zoho Invoice
        </Button>
      </Box>

      <Typography variant="h5" fontWeight="bold" sx={{ padding: 2, borderRadius: 2, backgroundColor: ColorData.primary.main, color: ColorData.text.secondary, mb: 3 }}>
        Case Control Center - #{caseData?.caseNumber || numericCaseId}
      </Typography>

      {caseData && (
        <Grid container spacing={3}>
          {/* Section A: Details */}
          <Grid size={{ xs: 12, md: 7 }}>
            <Paper sx={{ p: 3, height: "100%" }}>
              <Typography variant="h6" mb={2} color="primary">Case Details</Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box mb={1.5}>
                    <Typography variant="body2" color="textSecondary">Client Name</Typography>
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mt={0.5}>
                      <Typography variant="body1" fontWeight="600">{caseData.clientFullName}</Typography>
                      {caseData.userType && (
                        <Chip
                          label={caseData.userType}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ fontWeight: "600", fontSize: "0.75rem", textTransform: "capitalize", borderRadius: "6px" }}
                        />
                      )}
                    </Box>
                    {(caseData.claimantEmail || caseData.claimantPhone) && (
                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                        {caseData.claimantEmail !== "Not Provided" ? caseData.claimantEmail : ""}
                        {(caseData.claimantEmail !== "Not Provided" && caseData.claimantPhone !== "Not Provided" && caseData.claimantEmail && caseData.claimantPhone) ? ' | ' : ''}
                        {caseData.claimantPhone !== "Not Provided" ? caseData.claimantPhone : ""}
                      </Typography>
                    )}
                  </Box>
                </Grid>
                {caseData.respondentName && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Box mb={1.5}>
                      <Typography variant="body2" color="textSecondary">Respondent Details</Typography>
                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mt={0.5}>
                        <Typography variant="body1" fontWeight="600">{caseData.respondentName}</Typography>
                        {caseData.respondentUserType && (
                          <Chip
                            label={caseData.respondentUserType}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ fontWeight: "600", fontSize: "0.75rem", textTransform: "capitalize", borderRadius: "6px" }}
                          />
                        )}
                      </Box>
                      {(caseData.respondentEmail || caseData.respondentPhone) && (
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                          {caseData.respondentEmail !== "Not Provided" ? caseData.respondentEmail : ""}
                          {(caseData.respondentEmail !== "Not Provided" && caseData.respondentPhone !== "Not Provided" && caseData.respondentEmail && caseData.respondentPhone) ? ' | ' : ''}
                          {caseData.respondentPhone !== "Not Provided" ? caseData.respondentPhone : ""}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                )}
                <Grid size={{ xs: 12, sm: 6 }}><DetailItem label="Case Type" value={caseData.caseTypeName} /></Grid>
                <Grid size={{ xs: 12, sm: 6 }}><DetailItem label="Order Type" value={caseData.orderTypeName} /></Grid>
                <Grid size={{ xs: 12, sm: 6 }}><DetailItem label="Case Price" value={`₹${caseData.casePrice}`} /></Grid>
                <Grid size={{ xs: 12, sm: 6 }}><DetailItem label="Claim Amount" value={caseData.claimAmount || "—"} /></Grid>
                <Grid size={{ xs: 12, sm: 6 }}><DetailItem label="Agreement Date" value={caseData.agreementDate || "—"} /></Grid>
                <Grid size={{ xs: 12, sm: 6 }}><DetailItem label="Due Date" value={caseData.dueDateOfPayment || "—"} /></Grid>
                <Grid size={{ xs: 12, sm: 6 }}><DetailItem label="Created By" value={caseData.createdByUserName} /></Grid>
                <Grid size={{ xs: 12, sm: 6 }}><DetailItem label="Created On" value={new Date(caseData.dateCreated).toLocaleString()} /></Grid>
                <Grid size={{ xs: 12 }}><DetailItem label="Description" value={caseData.description || "N/A"} /></Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Right Side Stack */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Box display="flex" flexDirection="column" gap={3} height="100%">
              {/* Section B: Status Update */}
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" mb={2} color="primary">Update Status</Typography>
                <Box mb={2}>
                  <Typography variant="body2" color="textSecondary">Current Status</Typography>
                  <Chip label={caseData.currentProcessingStatusName || "Unknown"} color="info" />
                </Box>

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel id="status-select-label">Change Status</InputLabel>
                  <Select
                    labelId="status-select-label"
                    value={selectedStatusId}
                    label="Change Status"
                    onChange={(e) => setSelectedStatusId(e.target.value as number)}
                  >
                    {caseStatusList.map((s) => (
                      <MenuItem key={s.pkProcessingStatus} value={s.pkProcessingStatus}>
                        {s.statusName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  color="success"
                  fullWidth
                  onClick={handleUpdateStatus}
                  disabled={isUpdatingStatus || !selectedStatusId || selectedStatusId === caseData.currentProcessingStatusId}
                >
                  {isUpdatingStatus ? "Updating..." : "Save Status"}
                </Button>
              </Paper>

              {/* Section B2: SuperAdmin Admin Assignment */}
              {myInfo?.userType?.toUpperCase() === "SUPERADMIN" && (
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" mb={2} color="primary">Assign Admin to Case</Typography>
                  <Box mb={2}>
                    <Typography variant="body2" color="textSecondary">Current Assigned Admin</Typography>
                    <Chip label={caseData.assignedAdminName || "Unassigned"} color={caseData.assignedAdminName && caseData.assignedAdminName !== "Not Assigned" ? "success" : "warning"} />
                  </Box>

                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel id="admin-select-label">Select Admin</InputLabel>
                    <Select
                      labelId="admin-select-label"
                      value={selectedAdminId}
                      label="Select Admin"
                      onChange={(e) => setSelectedAdminId(e.target.value as number)}
                    >
                      {adminList.map((a: any) => (
                        <MenuItem key={a.id} value={a.id}>
                          {a.firstName} {a.lastName} ({a.email})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={handleAssignAdmin}
                    disabled={isUpdatingAdmin || !selectedAdminId}
                  >
                    {isUpdatingAdmin ? "Assigning..." : "Assign Admin"}
                  </Button>
                </Paper>
              )}

              {/* Section C: Extra Payments & Invoices */}
              <Paper sx={{ p: 3, flexGrow: 1 }}>
                <Typography variant="h6" mb={2} color="primary">Chat Box Payments</Typography>
                {casePayments.filter((payment, index) => {
                  // Differentiate Case Payment vs Chat Box Payment:
                  // The payments array is sorted descending by date (newest first).
                  // If the case had a non-zero price, the very first payment (the oldest, at the end of the array)
                  // is the initial case payment. We hide it to only show chat box payments.
                  if (caseData?.finalCasePrice && caseData.finalCasePrice > 0 && index === casePayments.length - 1) {
                    return false;
                  }
                  return true;
                }).length > 0 ? (
                  <Box display="flex" flexDirection="column" gap={2}>
                    {casePayments.filter((payment, index) => {
                      if (caseData?.finalCasePrice && caseData.finalCasePrice > 0 && index === casePayments.length - 1) {
                        return false;
                      }
                      return true;
                    }).map((payment) => (
                      <Box key={payment.pkPayment} display="flex" flexDirection="column" p={2} bgcolor="#f9f9f9" borderRadius="8px" border="1px solid #e0e0e0">
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <Typography variant="subtitle2" fontWeight="bold">
                            ₹{(payment.amount / 1.18).toFixed(2)} {/* Exclude 18% GST */}
                          </Typography>
                          <Chip label={payment.paymentStatus} size="small" color={payment.paymentStatus === "PaymentCompleted" || payment.paymentStatus === "Completed" ? "success" : "default"} />
                        </Box>
                        <Typography variant="caption" color="textSecondary" mb={1}>
                          Date: {new Date(payment.paymentDate).toLocaleDateString()}
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          color="warning"
                          onClick={() => { setSelectedPaymentForInvoice(payment); setOpenInvoiceModal(true); }}
                        >
                          Generate Invoice
                        </Button>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="textSecondary" sx={{ fontStyle: "italic" }}>
                    No extra payments found for this case.
                  </Typography>
                )}
              </Paper>
            </Box>
          </Grid>

          {/* Section C: Panel Management - Current Members */}
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" mb={2} color="primary">Assigned Panel Members</Typography>
              {(caseData.assignedProfessionals && caseData.assignedProfessionals.length > 0) || caseData.assignedProfessionalId ? (
                <Grid container spacing={2}>
                  {/* Primary Professional Fallback (if they exist but are NOT in the assignedProfessionals array) */}
                  {caseData.assignedProfessionalId && !caseData.assignedProfessionals?.some(p => p.professionalId === caseData.assignedProfessionalId) && (
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" p={2} bgcolor="#f9f9f9" borderRadius="8px" border="1px solid #e0e0e0">
                        <Box>
                          <Typography variant="subtitle1" fontWeight="600">{caseData.assignedProfessionalName}</Typography>
                          <Box display="flex" gap={1} mt={0.5}>
                            <Chip label="Primary Professional" size="small" variant="outlined" color="primary" />
                          </Box>
                        </Box>
                        <Button variant="outlined" color="error" size="small" onClick={() => handleUnassignProfessional(caseData.assignedProfessionalId as number)}>
                          Remove
                        </Button>
                      </Box>
                    </Grid>
                  )}
                  {/* Panel Professionals (Including Primary if they are in the array) */}
                  {caseData.assignedProfessionals?.map((prof) => (
                    <Grid size={{ xs: 12, md: 6 }} key={prof.professionalId}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" p={2} bgcolor="#f9f9f9" borderRadius="8px" border="1px solid #e0e0e0">
                        <Box>
                          <Typography variant="subtitle1" fontWeight="600">{prof.fullName}</Typography>
                          <Typography variant="body2" color="text.secondary">{prof.email} | {prof.contactNumber}</Typography>
                          <Box display="flex" gap={1} mt={0.5}>
                            {prof.professionalId === caseData.assignedProfessionalId && (
                              <Chip label="Primary" size="small" color="primary" />
                            )}
                            <Chip label={prof.role || "Panel Member"} size="small" variant="outlined" color="primary" />
                            <Chip label={prof.consentStatus || "Confirmed"} size="small" color={["Confirmed", "Accepted"].includes(prof.consentStatus) ? "success" : "warning"} />
                          </Box>
                        </Box>
                        <Button variant="outlined" color="error" size="small" onClick={() => handleUnassignProfessional(prof.professionalId)}>
                          Remove
                        </Button>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography variant="body2" color="textSecondary" sx={{ fontStyle: "italic" }}>
                  No professionals assigned to this case yet.
                </Typography>
              )}
            </Paper>
          </Grid>

          {/* Section D: Panel Management - Add Members */}
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" mb={2} color="primary">Add Professional to Case</Typography>

              <Grid container spacing={3} sx={{ mb: 2 }}>
                <Grid size={{ xs: 12, md: 8 }}>
                  <TextField
                    fullWidth
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search professional by name, email..."
                    variant="outlined"
                    slotProps={{
                      input: { startAdornment: <InputAdornment position="start"><SearchIcon color="primary" /></InputAdornment> }
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Chip icon={<PersonIcon />} label={`Eligible Users: ${displayedUsers.length}`} color="info" variant="outlined" sx={{ height: "100%" }} />
                </Grid>
              </Grid>

              <TableContainer sx={{ maxHeight: 400, overflowY: "auto", border: "1px solid #e0e0e0", borderRadius: 2 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>Name</StyledTableCell>
                      <StyledTableCell>Contact</StyledTableCell>
                      <StyledTableCell>Address</StyledTableCell>
                      <StyledTableCell align="center">Action</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayedUsers.length === 0 ? (
                      <TableRow><TableCell colSpan={4} align="center">No Users Found</TableCell></TableRow>
                    ) : (
                      displayedUsers.map((user: any) => {
                        const isAlreadyAssigned = caseData.assignedProfessionals?.some(ap => ap.professionalId === user.id);
                        return (
                          <TableRow key={user.id}>
                            <TableCell>{user.firstName} {user.lastName}</TableCell>
                            <TableCell>{user.contactNumber}<br />{user.email}</TableCell>
                            <TableCell>{formatUserAddress(user, IndianStates)}</TableCell>
                            <TableCell align="center">
                              {isAlreadyAssigned ? (
                                <Chip label="Already Assigned" color="success" size="small" />
                              ) : (
                                <Button
                                  variant="contained"
                                  size="small"
                                  startIcon={<AssignmentTurnedInIcon />}
                                  onClick={() => handleAssignProfessional(String(user.id))}
                                >
                                  Assign
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

            </Paper>
          </Grid>

        </Grid>
      )}

      {/* ZOHO INVOICE MODAL */}
      <Dialog open={openInvoiceModal} onClose={() => setOpenInvoiceModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: ColorData.primary.main, fontWeight: "bold" }}>Generate Zoho Invoice</DialogTitle>
        <DialogContent dividers>
          {selectedPaymentForInvoice ? (
            <Typography variant="body2" color="primary" fontWeight="bold" mb={2}>
              Chat Box Payment Amount: ₹{selectedPaymentForInvoice.amount}
            </Typography>
          ) : (
            <Typography variant="body2" color="textSecondary" mb={2}>
              Base Case Price: ₹{caseData?.casePrice}
            </Typography>
          )}
          <TextField
            label="Professional Fees (₹)"
            type="text"
            fullWidth
            margin="normal"
            size="small"
            value={professionalFees}
            onChange={(e) => setProfessionalFees(e.target.value ? Number(e.target.value) : "")}
          />
          <TextField
            label="NoLegalPaisa Commission (₹)"
            type="text"
            fullWidth
            margin="normal"
            size="small"
            value={nlpCommission}
            onChange={(e) => setNlpCommission(e.target.value ? Number(e.target.value) : "")}
          />
          <TextField
            label="Out-of-Pocket Expenses (₹)"
            type="text"
            fullWidth
            margin="normal"
            size="small"
            value={outOfPocketExpenses}
            onChange={(e) => setOutOfPocketExpenses(e.target.value ? Number(e.target.value) : "")}
          />
          <TextField
            label="Government Fees / Taxes (₹)"
            type="text"
            fullWidth
            margin="normal"
            size="small"
            value={governmentFees}
            onChange={(e) => setGovernmentFees(e.target.value ? Number(e.target.value) : "")}
          />
          <FormControlLabel
            control={<Checkbox checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} color="primary" />}
            label="Email Invoice to Customer Immediately"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenInvoiceModal(false)} color="inherit" disabled={isGeneratingInvoice}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleGenerateInvoice} disabled={isGeneratingInvoice}>
            {isGeneratingInvoice ? <CircularProgress size={24} /> : "Submit & Generate"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CaseControlCenter;
